import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaArrowLeft, FaHammer, FaServer, FaUserCircle, FaSignOutAlt, FaBell, FaBars,
  FaPlus, FaTrash, FaChevronDown, FaChevronRight, FaTimes, FaCommentAlt,
} from 'react-icons/fa';
import { getApiEndpoint } from '../../../config/api';
import { getToken, clearToken } from '../../../utils/auth';

/**
 * Building Tools — the internal build tracker for the CloudSafe team.
 *
 * Lives inside the standard cs-dashboard shell (header + sidebar + cs-content)
 * like every other Cloud Staff page, and uses the same GitHub-dark palette.
 *
 * Design bias throughout: adding a task must be faster than not bothering.
 * One always-visible input adds a headline item; every row has a "+" that adds
 * a child inline and stays open, because you're usually listing several at
 * once. The detail panel only opens when you want notes and dates.
 */

/* GitHub-dark palette, matching the rest of the Cloud Staff portal. */
const C = {
  bg: '#0d1117', panel: '#161b22', border: '#30363d', borderSoft: '#21262d',
  text: '#f0f6fc', dim: '#8b949e', blue: '#58a6ff', green: '#3fb950',
  amber: '#d29922', red: '#f85149', purple: '#a371f7',
};

const AREAS = ['Forms', 'Jobs', 'Billing', 'Tasks', 'Clients', 'Properties', 'Reports',
  'Field App', 'Portal', 'Calendar', 'Messaging', 'Admin', 'Infrastructure', 'Other'];

const STATUS = {
  idea:        { label: 'Idea',        fg: C.purple },
  todo:        { label: 'To do',       fg: C.dim },
  in_progress: { label: 'In progress', fg: C.blue },
  blocked:     { label: 'Blocked',     fg: C.red },
  done:        { label: 'Done',        fg: C.green },
};
const STATUS_ORDER = ['idea', 'todo', 'in_progress', 'blocked', 'done'];
const SIZES = { s: 'S', m: 'M', l: 'L' };

const api = async (path, opts = {}) => {
  const res = await fetch(getApiEndpoint(`/api/cloud-staff${path}`), {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...(opts.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message || 'Request failed');
  return data;
};

const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '');

const tag = (fg, bg) => ({
  background: bg || 'rgba(255,255,255,0.05)', color: fg, fontSize: '0.62rem', fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.03em', padding: '0.12rem 0.42rem',
  borderRadius: 5, whiteSpace: 'nowrap',
});
const field = {
  width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7,
  padding: '0.5rem 0.65rem', fontSize: '0.85rem', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', outline: 'none',
};
const lbl = { fontSize: '0.7rem', fontWeight: 600, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.03em' };

const BuildingTools = () => {
  const navigate = useNavigate();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({});
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [q, setQ] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [areaFilter, setAreaFilter] = useState('all');
  const [hideDone, setHideDone] = useState(true);

  const [quick, setQuick] = useState('');
  const [quickArea, setQuickArea] = useState('');
  const [addingUnder, setAddingUnder] = useState(null);
  const [childTitle, setChildTitle] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [openId, setOpenId] = useState(null);
  const childRef = useRef(null);

  const userName = localStorage.getItem('userName') || 'Cloud Staff';
  const handleLogout = () => { clearToken(); navigate('/login'); };

  const load = useCallback(async () => {
    try {
      const d = await api('/build-tasks');
      setTasks(d?.tasks || []);
      setCounts(d?.counts || {});
      setErr(null);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api('/build-tasks/meta/owners').then((d) => setOwners(d?.owners || [])).catch(() => {}); }, []);
  useEffect(() => { if (addingUnder && childRef.current) childRef.current.focus(); }, [addingUnder]);

  const patch = async (id, body) => {
    setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, ...body } : t)));  // optimistic
    try { await api(`/build-tasks/${id}`, { method: 'PUT', body: JSON.stringify(body) }); }
    catch (e) { setErr(e.message); }
    load();
  };

  const addTask = async (body) => {
    try { await api('/build-tasks', { method: 'POST', body: JSON.stringify(body) }); await load(); }
    catch (e) { setErr(e.message); }
  };

  const removeTask = async (t) => {
    const kids = tasks.filter((x) => String(x.path || '').includes(`/${t.id}/`)).length;
    if (!window.confirm(kids ? `Delete "${t.title}" and its ${kids} sub-task${kids === 1 ? '' : 's'}?` : `Delete "${t.title}"?`)) return;
    try { await api(`/build-tasks/${t.id}`, { method: 'DELETE' }); await load(); }
    catch (e) { setErr(e.message); }
  };

  /* ------------------------------------------------------------ tree build */

  // A matching child must keep its parents visible, or the tree collapses into
  // orphaned rows with no context.
  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    const matches = (t) => {
      if (hideDone && t.status === 'done') return false;
      if (ownerFilter === 'unassigned' && t.owner_id) return false;
      if (ownerFilter !== 'all' && ownerFilter !== 'unassigned' && String(t.owner_id) !== ownerFilter) return false;
      if (areaFilter !== 'all' && t.area !== areaFilter) return false;
      if (term && !`${t.title} ${t.detail || ''}`.toLowerCase().includes(term)) return false;
      return true;
    };
    const keep = new Set();
    tasks.forEach((t) => {
      if (!matches(t)) return;
      keep.add(t.id);
      String(t.path || '').split('/').filter(Boolean).forEach((p) => keep.add(Number(p)));
    });
    return keep;
  }, [tasks, q, ownerFilter, areaFilter, hideDone]);

  const byParent = useMemo(() => {
    const m = new Map();
    tasks.forEach((t) => {
      if (!visible.has(t.id)) return;
      const k = t.parent_id || 0;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(t);
    });
    m.forEach((list) => list.sort((a, b) => {
      const ap = a.priority ?? 9999, bp = b.priority ?? 9999;   // unset sinks
      return ap !== bp ? ap - bp : (a.sort_order - b.sort_order) || (a.id - b.id);
    }));
    return m;
  }, [tasks, visible]);

  const childCount = useCallback((id) => {
    const kids = tasks.filter((t) => t.parent_id === id);
    return { total: kids.length, done: kids.filter((k) => k.status === 'done').length };
  }, [tasks]);

  const toggleCollapse = (id) => setCollapsed((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  /* ---------------------------------------------------------------- render */

  const Row = ({ t, depth }) => {
    const st = STATUS[t.status] || STATUS.todo;
    const kids = childCount(t.id);
    const isCollapsed = collapsed.has(t.id);
    const overdue = t.status !== 'done' && t.due_date && t.due_date < today();
    const done = t.status === 'done';

    return (
      <>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', paddingLeft: 12 + depth * 22,
            borderBottom: `1px solid ${C.borderSoft}`,
            background: openId === t.id ? 'rgba(88,166,255,0.07)' : 'transparent',
            opacity: done ? 0.5 : 1,
          }}
        >
          <button onClick={() => kids.total && toggleCollapse(t.id)}
            style={{ background: 'none', border: 'none', width: 16, color: C.dim, padding: 0,
                     cursor: kids.total ? 'pointer' : 'default', fontSize: '0.65rem', flexShrink: 0 }}>
            {kids.total ? (isCollapsed ? <FaChevronRight /> : <FaChevronDown />) : null}
          </button>

          <input type="checkbox" checked={done} title="Mark done"
            onChange={() => patch(t.id, { status: done ? 'todo' : 'done' })}
            style={{ flexShrink: 0, cursor: 'pointer', accentColor: C.green }} />

          {t.priority != null && (
            <span title="Priority" style={{ ...tag(C.blue, 'rgba(88,166,255,0.12)'), minWidth: 20, textAlign: 'center' }}>
              {t.priority}
            </span>
          )}

          <span onClick={() => setOpenId(t.id)}
            style={{ flex: 1, minWidth: 0, cursor: 'pointer', fontSize: '0.86rem', color: C.text,
                     textDecoration: done ? 'line-through' : 'none',
                     overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t.title}
            {kids.total > 0 && (
              <span style={{ color: C.dim, fontSize: '0.74rem', marginLeft: 8 }}>{kids.done}/{kids.total}</span>
            )}
            {Number(t.note_count) > 0 && (
              <FaCommentAlt title={`${t.note_count} note(s)`} style={{ color: C.dim, fontSize: '0.6rem', marginLeft: 8, opacity: 0.6 }} />
            )}
          </span>

          {t.area && <span style={tag(C.dim)}>{t.area}</span>}
          {t.size && <span style={tag(C.dim)}>{SIZES[t.size]}</span>}
          {t.due_date && (
            <span style={{ fontSize: '0.74rem', color: overdue ? C.red : C.dim, fontWeight: overdue ? 700 : 400, whiteSpace: 'nowrap' }}>
              {fmtDate(t.due_date)}
            </span>
          )}
          <span style={tag(st.fg)}>{st.label}</span>
          <span title={t.owner_name || 'Unassigned'}
            style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                     background: t.owner_name ? 'rgba(88,166,255,0.15)' : 'rgba(255,255,255,0.04)',
                     color: t.owner_name ? C.blue : C.dim, fontSize: '0.6rem', fontWeight: 700,
                     display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {t.owner_name ? t.owner_name.split(/\s+/).slice(0, 2).map((s) => s[0]).join('').toUpperCase() : '–'}
          </span>

          <button title="Add sub-task"
            onClick={() => { setAddingUnder(t.id); setChildTitle(''); setCollapsed((s) => { const n = new Set(s); n.delete(t.id); return n; }); }}
            style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', padding: '2px 4px', flexShrink: 0, fontSize: '0.75rem' }}>
            <FaPlus />
          </button>
          <button title="Delete" onClick={() => removeTask(t)}
            style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', padding: '2px 4px', flexShrink: 0, fontSize: '0.72rem' }}>
            <FaTrash />
          </button>
        </div>

        {addingUnder === t.id && (
          <div style={{ display: 'flex', gap: 8, padding: '8px 12px', paddingLeft: 12 + (depth + 1) * 22,
                        background: 'rgba(255,255,255,0.02)', borderBottom: `1px solid ${C.borderSoft}` }}>
            <input ref={childRef} style={{ ...field, flex: 1 }} value={childTitle}
              placeholder="Sub-task — Enter to add, Esc to close"
              onChange={(e) => setChildTitle(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Escape') { setAddingUnder(null); setChildTitle(''); }
                if (e.key === 'Enter' && childTitle.trim()) {
                  await addTask({ title: childTitle.trim(), parent_id: t.id });
                  setChildTitle('');   // stay open — usually adding several
                }
              }} />
            <button onClick={() => { setAddingUnder(null); setChildTitle(''); }}
              style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', fontSize: '0.8rem' }}>Done</button>
          </div>
        )}

        {!isCollapsed && (byParent.get(t.id) || []).map((c) => <Row key={c.id} t={c} depth={depth + 1} />)}
      </>
    );
  };

  const roots = byParent.get(0) || [];
  const Stat = ({ n, label, fg }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
      <strong style={{ color: fg, fontSize: '1.05rem' }}>{n || 0}</strong>
      <span style={{ color: C.dim, fontSize: '0.76rem' }}>{label}</span>
    </div>
  );

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge"><FaServer /><span>Cloud Staff Portal</span></div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn"><FaBell /></button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header">
                    <FaUserCircle className="cs-menu-avatar" />
                    <div><p className="cs-menu-name">{userName}</p><p className="cs-menu-role">Cloud Staff Admin</p></div>
                  </div>
                  <div className="cs-user-menu-divider" />
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout"><FaSignOutAlt /> Sign Out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar active="building-tools" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />

        <main className="cs-content">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        marginBottom: '1.25rem', paddingBottom: '1rem', borderBottom: `1px solid ${C.borderSoft}`, gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Link to="/cloud-staff/dashboard" className="act-back-btn"><FaArrowLeft /></Link>
              <div>
                <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: C.text, margin: 0 }}>
                  <FaHammer style={{ marginRight: '0.5rem', color: C.amber }} /> Build Tracker
                </h1>
                <p style={{ fontSize: '0.8rem', color: C.dim, margin: '0.2rem 0 0' }}>
                  What we're building, who's on it, and what's next. Internal only.
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Stat n={(counts.total || 0) - (counts.done || 0)} label="open" fg={C.text} />
              <Stat n={counts.in_progress} label="in progress" fg={C.blue} />
              <Stat n={counts.blocked} label="blocked" fg={C.red} />
              <Stat n={counts.overdue} label="overdue" fg={C.amber} />
              <Stat n={counts.ideas} label="ideas" fg={C.purple} />
            </div>
          </div>

          {/* Always-visible capture box — the most important element on the page. */}
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...field, flex: 1 }} value={quick}
              placeholder="What needs building? Type it and press Enter…"
              onChange={(e) => setQuick(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && quick.trim()) {
                  await addTask({ title: quick.trim(), area: quickArea || null });
                  setQuick('');
                }
              }} />
            <select style={{ ...field, width: 'auto' }} value={quickArea} onChange={(e) => setQuickArea(e.target.value)}>
              <option value="">Area…</option>
              {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <input style={{ ...field, width: 190 }} value={q} placeholder="Search…" onChange={(e) => setQ(e.target.value)} />
            <select style={{ ...field, width: 'auto' }} value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
              <option value="all">Anyone</option>
              <option value="unassigned">Unassigned</option>
              {owners.map((o) => <option key={o.id} value={String(o.id)}>{o.name || o.email}</option>)}
            </select>
            <select style={{ ...field, width: 'auto' }} value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}>
              <option value="all">All areas</option>
              {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: C.dim, cursor: 'pointer' }}>
              <input type="checkbox" checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} style={{ accentColor: C.blue }} /> Hide done
            </label>
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: C.dim }}>
              Sorted by priority — set a number on any task
            </span>
          </div>

          {err && (
            <div style={{ marginTop: 12, fontSize: '0.8rem', color: C.red, background: 'rgba(248,81,73,0.1)',
                          border: `1px solid rgba(248,81,73,0.3)`, borderRadius: 8, padding: '8px 11px' }}>
              {err}
            </div>
          )}

          <div style={{ marginTop: 14, border: `1px solid ${C.border}`, borderRadius: 10,
                        overflow: 'hidden', background: C.panel }}>
            {loading ? (
              <div style={{ padding: 30, color: C.dim, fontSize: '0.85rem' }}>Loading…</div>
            ) : roots.length === 0 ? (
              <div style={{ padding: '50px 20px', textAlign: 'center', color: C.dim }}>
                <FaHammer style={{ fontSize: '1.6rem', opacity: 0.3, marginBottom: 10 }} />
                <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>
                  {tasks.length ? 'Nothing matches those filters' : 'Nothing on the list yet'}
                </div>
                <div style={{ fontSize: '0.84rem' }}>
                  {tasks.length ? 'Try clearing the search or filters.' : 'Type in the box above to add the first thing.'}
                </div>
              </div>
            ) : (
              roots.map((t) => <Row key={t.id} t={t} depth={0} />)
            )}
          </div>
        </main>
      </div>

      {openId && (
        <TaskPanel id={openId} owners={owners} onClose={() => setOpenId(null)} onChanged={load} />
      )}
    </div>
  );
};

/** Detail panel — dates, owner, priority and the notes thread. */
const TaskPanel = ({ id, owners, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api(`/build-tasks/${id}`)); } catch { /* panel shows loading */ }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = async (body) => {
    setSaving(true);
    try { await api(`/build-tasks/${id}`, { method: 'PUT', body: JSON.stringify(body) }); await load(); onChanged(); }
    catch { /* keep the panel open */ }
    finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api(`/build-tasks/${id}/notes`, { method: 'POST', body: JSON.stringify({ body: note }) });
    setNote(''); load(); onChanged();
  };

  const t = data?.task;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(1,4,9,0.7)', zIndex: 1200 }} />
      <aside style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(520px, 100vw)',
                      background: C.panel, borderLeft: `1px solid ${C.border}`,
                      boxShadow: '-8px 0 30px rgba(0,0,0,0.5)', zIndex: 1201, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          {data?.ancestors?.length > 0 && (
            <div style={{ fontSize: '0.72rem', color: C.dim, marginBottom: 4 }}>
              {data.ancestors.map((a) => a.title).join(' › ')}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
            <input defaultValue={t?.title || ''} key={t?.id}
              onBlur={(e) => e.target.value.trim() && e.target.value !== t?.title && save({ title: e.target.value })}
              style={{ ...field, border: 'none', background: 'transparent', padding: 0, fontSize: '1rem', fontWeight: 700 }} />
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', fontSize: '0.9rem' }}>
              <FaTimes />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {!t ? <div style={{ color: C.dim, fontSize: '0.85rem' }}>Loading…</div> : (
            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={lbl}>Status
                  <select style={{ ...field, marginTop: 4 }} value={t.status} onChange={(e) => save({ status: e.target.value })}>
                    {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
                  </select>
                </label>
                <label style={lbl}>Owner
                  <select style={{ ...field, marginTop: 4 }} value={t.owner_id || ''} onChange={(e) => save({ owner_id: e.target.value || null })}>
                    <option value="">Unassigned</option>
                    {owners.map((o) => <option key={o.id} value={o.id}>{o.name || o.email}</option>)}
                  </select>
                </label>
                <label style={lbl}>Priority (1 = first)
                  <input style={{ ...field, marginTop: 4 }} defaultValue={t.priority ?? ''} inputMode="numeric" key={`p${t.id}`}
                    onBlur={(e) => save({ priority: e.target.value })} />
                </label>
                <label style={lbl}>Deadline
                  <input type="date" style={{ ...field, marginTop: 4, colorScheme: 'dark' }} value={t.due_date || ''}
                    onChange={(e) => save({ due_date: e.target.value })} />
                </label>
                <label style={lbl}>Area
                  <select style={{ ...field, marginTop: 4 }} value={t.area || ''} onChange={(e) => save({ area: e.target.value })}>
                    <option value="">—</option>
                    {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </label>
                <label style={lbl}>Size
                  <select style={{ ...field, marginTop: 4 }} value={t.size || ''} onChange={(e) => save({ size: e.target.value })}>
                    <option value="">—</option>
                    <option value="s">S — quick</option>
                    <option value="m">M — a day or two</option>
                    <option value="l">L — a chunk of work</option>
                  </select>
                </label>
              </div>

              {t.status === 'blocked' && (
                <label style={lbl}>What's blocking it
                  <input style={{ ...field, marginTop: 4 }} defaultValue={t.blocked_reason || ''} key={`b${t.id}`}
                    onBlur={(e) => save({ blocked_reason: e.target.value })} />
                </label>
              )}

              <label style={lbl}>Detail
                <textarea style={{ ...field, marginTop: 4, minHeight: 80, resize: 'vertical' }}
                  defaultValue={t.detail || ''} key={`d${t.id}`} placeholder="What is this, and what does done look like?"
                  onBlur={(e) => save({ detail: e.target.value })} />
              </label>

              <div>
                <div style={{ ...lbl, marginBottom: 6 }}>
                  Notes {saving ? <span style={{ color: C.dim, fontWeight: 400, textTransform: 'none' }}>· saving…</span> : null}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={field} value={note} placeholder="Add a note…"
                    onChange={(e) => setNote(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }} />
                  <button onClick={addNote} disabled={!note.trim()}
                    style={{ background: C.blue, color: '#0d1117', border: 'none', borderRadius: 7, padding: '0 14px',
                             fontSize: '0.82rem', fontWeight: 700, cursor: note.trim() ? 'pointer' : 'default', opacity: note.trim() ? 1 : 0.4 }}>
                    Add
                  </button>
                </div>
                <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
                  {(data.notes || []).map((n) => (
                    <div key={n.id} style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 8, padding: '9px 11px', background: C.bg }}>
                      <div style={{ fontSize: '0.7rem', color: C.dim, marginBottom: 3 }}>
                        {n.author_name || 'Someone'} · {new Date(n.created_at).toLocaleString('en-GB')}
                      </div>
                      <div style={{ fontSize: '0.83rem', color: C.text, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{n.body}</div>
                    </div>
                  ))}
                  {(data.notes || []).length === 0 && (
                    <div style={{ fontSize: '0.8rem', color: C.dim, padding: '6px 0' }}>No notes yet.</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default BuildingTools;
